import PropTypes from 'prop-types';
import React from 'react';
import { useIntl } from 'react-intl';
import { Col, Row } from 'reactstrap';

import {
  BUY, SELL, SectionHeader, SectionWrapper,
  UNIT_CARBON, UNIT_CURRENCY, UNIT_ENERGY,
} from 'src/enosikit/components/Chart/components/chartConstants';
import { getDirectionHeaderLabel } from 'src/enosikit/components/Chart/helpers/cards';
import { extractTradeValues, getTooltipFormatFunc } from 'src/enosikit/components/Chart/helpers/tooltip';
import { isOtherSubSeriesKey } from 'src/util/chartFiltering';
import {
  DATA_AGGREGATE_BY_METER, DATA_AGGREGATE_BY_PROPERTY,
  DATA_GROUP_BY_COUNTERPARTY, DATA_GROUP_BY_TRADE_TYPE,
} from 'src/util/constants';
import getMeterLabel from 'src/util/meter';

import { getEnergyOriginLabel } from '../helpers/propertyShowChart';

/**
 * Renders the Property trade chart tooltip, preserving meter and counterparty labels.
 * @param {object} props Component props.
 * @param {object} props.chartView Property chart aggregation and grouping options.
 * @param {object} props.data Buy and sell trade tooltip data.
 * @param {string} props.unit Chart unit.
 * @param {string} props.unitLabel Unit display label.
 * @returns {React.ReactElement|null} Property trade tooltip content.
 */
export default function TradeChartTooltip({
  chartView = null, data = null, unit = null, unitLabel = null,
}) {
  const intl = useIntl();

  if (!data || !chartView) {
    return null;
  }

  const values = extractTradeValues(data);
  const tooltipFormatFunc = getTooltipFormatFunc(unit, values);
  const aggByMeter = chartView.aggregateBy === DATA_AGGREGATE_BY_METER;

  return (
    <>
      {[BUY, SELL].map((direction) => {
        if (!data[direction] || Object.keys(data[direction]).length === 0) {
          return null;
        }

        const directionHeader = getDirectionHeaderLabel(intl, direction);

        return (
          <SectionWrapper key={direction}>
            {!aggByMeter && (
              <SectionHeader>
                {directionHeader}
              </SectionHeader>
            )}

            {Object.keys(data[direction]).map((key) => {
              const datum = data[direction][key];
              if (!datum) {
                return null;
              }

              const {
                key: seriesKey, label, meter, subLabel, value,
              } = datum;
              const meterLabel = getMeterLabel(meter?.identifier, meter?.title);
              const energyOriginLabel = isOtherSubSeriesKey(seriesKey)
                ? label
                : getEnergyOriginLabel(intl, label, chartView);

              return (
                <React.Fragment key={key}>
                  {aggByMeter && (
                    <SectionHeader>
                      {meterLabel
                        ? `${directionHeader} - ${meterLabel}`
                        : directionHeader}
                    </SectionHeader>
                  )}
                  <Row className="mb-1">
                    <Col className="pe-0">
                      <span>{energyOriginLabel}</span>
                      {subLabel && (
                        <>
                          <br />
                          <small>
                            {`(${subLabel})`}
                          </small>
                        </>
                      )}
                    </Col>
                    <Col xs="auto">
                      {tooltipFormatFunc(value, unitLabel)}
                    </Col>
                  </Row>
                </React.Fragment>
              );
            })}
          </SectionWrapper>
        );
      })}
    </>
  );
}

TradeChartTooltip.propTypes = {
  chartView: PropTypes.shape({
    aggregateBy: PropTypes.oneOf([
      DATA_AGGREGATE_BY_METER,
      DATA_AGGREGATE_BY_PROPERTY,
    ]),
    groupBy: PropTypes.oneOf([
      DATA_GROUP_BY_COUNTERPARTY,
      DATA_GROUP_BY_TRADE_TYPE,
    ]),
  }),
  data: PropTypes.shape({
    buy: PropTypes.arrayOf(PropTypes.shape({
      key: PropTypes.string,
      label: PropTypes.string,
      meter: PropTypes.shape({
        identifier: PropTypes.string,
        title: PropTypes.string,
      }),
      property: PropTypes.shape({}),
      subLabel: PropTypes.string,
      user: PropTypes.shape({}),
      value: PropTypes.number,
    })),
    sell: PropTypes.arrayOf(PropTypes.shape({
      key: PropTypes.string,
      label: PropTypes.string,
      meter: PropTypes.shape({
        identifier: PropTypes.string,
        title: PropTypes.string,
      }),
      property: PropTypes.shape({}),
      subLabel: PropTypes.string,
      user: PropTypes.shape({}),
      value: PropTypes.number,
    })),
  }),
  unit: PropTypes.oneOf([UNIT_CARBON, UNIT_CURRENCY, UNIT_ENERGY]),
  unitLabel: PropTypes.string,
};
